import styled from "@emotion/styled";
import { Alert, TablePagination } from "~/common/mui";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { C_OUTLINE_VARIANT, C_SURFACE } from "~/common/components/icon/Colors";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { useComponentSize } from "~/common/hooks/useComponentSize";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import type { TabularViewData } from "~/features/search/models/models";
import {
  selectHasTubularResults,
  selectTabularViewData,
  selectVisibleTabularViewColumns,
} from "~/features/search/redux/searchSelectors";
import { calculateTotalTableWidth, makeTechValueColumn, tsmakeNewDesignProductColumn } from "./TanStackColumnDefs";
import { useSearchPagination } from "../../useSearchPagination";
import { ELEVATION_5 } from "~/common/components/icon/Elevation";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { memo, useMemo } from "react";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

const TABLE_PINNING = { columnPinning: { left: ["name"] } };

interface Props {
  maxColumnAmount?: number;
}

export const NewDesignReduxProductTabularView = ({ maxColumnAmount }: Props) => {
  const { ref, width } = useComponentSize<HTMLDivElement>();
  const isMinTablet = useIsMinTabletSize();
  const screenSize = useNumericScreenSize();
  const isDesktop = screenSize >= useNumericScreenSize.desktopS;
  const isMinDesktopM = screenSize >= useNumericScreenSize.desktopM;
  const { t } = useTranslation();
  const unitMode = useUnitMode();

  const fullConfig = useSelector(selectVisibleTabularViewColumns);
  const results = useSelector(selectTabularViewData);

  const { pageAmount, currentPage, onPageChanged, totalResults } = useSearchPagination();
  const rowsPerPage = 20;
  const start = currentPage * rowsPerPage + 1;
  const end = Math.min((currentPage + 1) * rowsPerPage, totalResults);

  // Remember to add unitMode from useUnit to dependency if memoized
  const tanstackColumns: ColumnDef<TabularViewData>[] = useMemo(() => {
    const config = fullConfig?.filter((col) => col.visible).slice(0, maxColumnAmount) ?? [];
    return [
      tsmakeNewDesignProductColumn(
        t("page.search.tabular.products.title", {
          start: NF.getDefaultFormatter().format(start),
          end: NF.getDefaultFormatter().format(end),
          total: NF.getDefaultFormatter().format(totalResults),
        }),
        isMinDesktopM,
      ),
      ...config.map((col) => makeTechValueColumn(col)),
    ];
  }, [fullConfig, start, end, totalResults, t, unitMode, maxColumnAmount, isMinDesktopM]);

  const totalTableWidth = calculateTotalTableWidth(tanstackColumns);
  const isTableLargerThanDesktop = totalTableWidth > width;

  const hasTubularResults = useSelector(selectHasTubularResults);

  const state = TABLE_PINNING;
  let fullWidth = true;
  if (!isMinTablet) {
    // state = TABLE_PINNING;
    // fullWidth = false;
  } else if (!isDesktop || isTableLargerThanDesktop) {
    // state = TABLE_PINNING;
    // fullWidth = false;
  } else {
    const availableWidth = width - tanstackColumns[0].size!;
    const oneCol = (availableWidth - tanstackColumns.length) / Math.max(tanstackColumns.length - 1, 1);

    tanstackColumns.slice(1, tanstackColumns.length).forEach((c) => (c.size = oneCol));
    fullWidth = false;
  }

  const table = (
    <NewSRPTable
      data={results}
      scrollbarOnTop={true}
      columns={tanstackColumns}
      fullwidth={fullWidth}
      variant={""}
      state={state}
      stickyHeader={true}
    />
  );

  return (
    <div ref={ref}>
      {!hasTubularResults && (
        <Alert severity={"info"} className={"scha__srp_pdp_tubular_no-results mb-2"}>
          {t("srp.plp.tubularView.noResults")}
        </Alert>
      )}
      {width > 0 && table}
      {pageAmount > 0 && (
        <StyledTablePagination
          component="div"
          count={totalResults}
          page={currentPage}
          onPageChange={(_, page) => onPageChanged(page)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          labelDisplayedRows={({ from, to, count }) =>
            `${NF.getDefaultFormatter().format(from)}-${NF.getDefaultFormatter().format(
              to,
            )} of ${NF.getDefaultFormatter().format(count)}`
          }
        />
      )}
    </div>
  );
};

const StyledTablePagination = styled(TablePagination)<{ component: React.ElementType }>({
  background: C_SURFACE,
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderTop: `none`,
});

export const NewSRPTable = styled(memo(TanStackTable))({
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderBottom: `none !important`,

  table: {
    borderCollapse: "separate",
  },
  thead: {
    background: C_SURFACE,
  },
  td: {
    padding: "8px !important",
    border: `1px solid ${C_OUTLINE_VARIANT}`,
    borderLeft: `none !important`,
    borderTop: `none !important`,
    ["&:last-child"]: {
      borderRight: `none !important`,
    },
  },
  th: {
    verticalAlign: "middle",
    border: `1px solid ${C_OUTLINE_VARIANT} !important`,
    borderLeft: `none !important`,
    borderTop: `none !important`,

    ["&:last-child"]: {
      borderRight: `none !important`,
    },
  },

  [".is-scrolled .pinned-left-last"]: {
    boxShadow: ELEVATION_5,
  },

  [".scroll-slider__root"]: {
    backgroundColor: C_SURFACE,
  },
}) as typeof TanStackTable;
